import { t } from '../i18n/translations';
import { Poll, Role } from '../types';

export class PollsDrawer {
  private polls: Poll[] = [];
  private currentUserId = '';
  private currentUserRole: Role = 'participant';
  private containerEl: HTMLElement | null = null;
  private isVisible = false;
  private showCreateForm = false;

  public onCreatePoll?: (question: string, options: string[], isAnonymous: boolean) => void;
  public onVote?: (pollId: string, optionIdx: number) => void;
  public onClosePoll?: (pollId: string) => void;
  public onClose?: () => void;

  public setCurrentUser(id: string, role: Role) {
    this.currentUserId = id;
    this.currentUserRole = role;
    if (this.currentUserId) { /* bound */ }
  }

  public getPolls(): Poll[] {
    return this.polls;
  }

  public setPolls(polls: Poll[]) {
    this.polls = polls || [];
    if (this.isVisible && this.containerEl) {
      this.render(this.containerEl);
    }
  }

  public addPoll(poll: Poll) {
    if (!poll) return;
    const exists = this.polls.some(p => p.id === poll.id);
    if (!exists) {
      this.polls.unshift(poll);
    }
    if (this.isVisible && this.containerEl) {
      this.render(this.containerEl);
    }
  }

  public updatePoll(poll: Poll) {
    if (!poll) return;
    const idx = this.polls.findIndex(p => p.id === poll.id);
    if (idx >= 0) {
      this.polls[idx] = poll;
    } else {
      this.polls.unshift(poll);
    }
    if (this.isVisible && this.containerEl) {
      this.render(this.containerEl);
    }
  }

  public toggle(container: HTMLElement) {
    this.isVisible = !this.isVisible;
    if (this.isVisible) {
      this.render(container);
    } else {
      container.innerHTML = '';
    }
  }

  public hide() {
    this.isVisible = false;
    if (this.containerEl) {
      this.containerEl.innerHTML = '';
    }
  }

  public getIsOpen(): boolean {
    return this.isVisible;
  }

  public render(container: HTMLElement) {
    this.containerEl = container;
    const isHostOrMod = this.currentUserRole === 'host' || this.currentUserRole === 'moderator';

    container.innerHTML = `
      <div class="side-drawer">
        <div class="drawer-header">
          <h3>${t('polls')}</h3>
          <div style="display: flex; gap: 8px;">
            ${isHostOrMod && !this.showCreateForm ? `
              <button id="btn-show-create-poll" class="btn-primary" style="padding: 4px 10px; font-size: 0.8rem; width: auto;">
                + New
              </button>
            ` : ''}
            <button id="btn-close-polls" class="control-btn" style="width: 32px; height: 32px;">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        </div>

        <div class="drawer-body">
          ${this.showCreateForm ? this.renderCreateFormHTML() : ''}

          ${this.polls.length === 0 && !this.showCreateForm ? `
            <div style="text-align: center; color: var(--text-muted); padding: 3rem 1rem;">
              <p>No active polls yet.</p>
              ${isHostOrMod ? `<p style="font-size: 0.85rem; margin-top: 6px;">Click "+ New" to ask a question.</p>` : ''}
            </div>
          ` : ''}

          <div style="display: flex; flex-direction: column; gap: 16px;">
            ${this.polls.map(p => this.renderPollCardHTML(p, isHostOrMod)).join('')}
          </div>
        </div>
      </div>
    `;

    this.bindEvents(container);
  }

  private renderCreateFormHTML(): string {
    return `
      <div style="background: var(--bg-surface-elevated); border: 1px solid var(--border-subtle); border-radius: var(--radius-lg); padding: 14px; margin-bottom: 12px;">
        <h4 style="font-size: 0.9rem; margin-bottom: 10px;">${t('createPoll')}</h4>
        <form id="form-new-poll">
          <div class="form-group">
            <input type="text" id="poll-question-input" class="form-input" placeholder="${t('pollQuestion')}" required maxlength="150" />
          </div>
          <div class="form-group">
            <input type="text" class="form-input poll-option-input" placeholder="Option 1" required maxlength="80" />
          </div>
          <div class="form-group">
            <input type="text" class="form-input poll-option-input" placeholder="Option 2" required maxlength="80" />
          </div>
          <div class="form-group">
            <input type="text" class="form-input poll-option-input" placeholder="Option 3 (Optional)" maxlength="80" />
          </div>
          <div class="form-group">
            <label class="form-checkbox">
              <input type="checkbox" id="check-poll-anon" style="accent-color: var(--brand-primary);" />
              <span>Anonymous voting</span>
            </label>
          </div>
          <div style="display: flex; gap: 8px; margin-top: 8px;">
            <button type="submit" class="btn-primary" style="flex: 1; padding: 8px;">Launch</button>
            <button type="button" id="btn-cancel-create-poll" class="btn-secondary" style="padding: 8px;">Cancel</button>
          </div>
        </form>
      </div>
    `;
  }

  private renderPollCardHTML(poll: Poll, isHostOrMod: boolean): string {
    let totalVotes = 0;
    if (poll.vote_counts) {
      for (const count of Object.values(poll.vote_counts)) {
        totalVotes += (count as number) || 0;
      }
    }

    return `
      <div class="poll-card">
        <div style="display: flex; justify-content: space-between; align-items: flex-start;">
          <h4 style="font-size: 0.95rem; line-height: 1.4;">${poll.question}</h4>
          ${poll.is_closed ? `
            <span class="badge" style="background: var(--color-danger-bg); color: var(--color-danger); font-size: 0.7rem;">Closed</span>
          ` : `
            <span class="badge" style="background: var(--color-success-bg); color: var(--color-success); font-size: 0.7rem;">Active</span>
          `}
        </div>

        <div style="display: flex; flex-direction: column; gap: 8px;">
          ${poll.options.map((opt, idx) => {
            const count = poll.vote_counts ? ((poll.vote_counts as any)[idx] ?? (poll.vote_counts as any)[String(idx)] ?? 0) : 0;
            const pct = totalVotes > 0 ? Math.round((count / totalVotes) * 100) : 0;

            return `
              <button class="poll-option-btn btn-vote-option" data-poll-id="${poll.id}" data-opt-idx="${idx}" ${poll.is_closed ? 'disabled' : ''}>
                <div class="poll-option-fill" style="width: ${pct}%;"></div>
                <span>${opt}</span>
                <span style="font-size: 0.8rem; font-weight: 700; color: var(--text-secondary);">${pct}% (${count})</span>
              </button>
            `;
          }).join('')}
        </div>

        <div style="display: flex; justify-content: space-between; align-items: center; font-size: 0.75rem; color: var(--text-muted); margin-top: 4px;">
          <span>${totalVotes} total votes ${poll.is_anonymous ? '• Anonymous' : ''}</span>
          ${isHostOrMod && !poll.is_closed ? `
            <button class="btn-close-poll" data-poll-id="${poll.id}" style="border: none; background: transparent; color: var(--color-danger); cursor: pointer; font-size: 0.75rem; font-weight: 600;">
              ${t('closePoll')}
            </button>
          ` : ''}
        </div>
      </div>
    `;
  }

  private bindEvents(container: HTMLElement) {
    container.querySelector('#btn-close-polls')?.addEventListener('click', () => {
      this.hide();
      if (this.onClose) this.onClose();
    });

    container.querySelector('#btn-show-create-poll')?.addEventListener('click', () => {
      this.showCreateForm = true;
      this.render(container);
    });

    container.querySelector('#btn-cancel-create-poll')?.addEventListener('click', () => {
      this.showCreateForm = false;
      this.render(container);
    });

    container.querySelector('#form-new-poll')?.addEventListener('submit', (e) => {
      e.preventDefault();
      const qInput = container.querySelector('#poll-question-input') as HTMLInputElement;
      const optInputs = container.querySelectorAll('.poll-option-input') as NodeListOf<HTMLInputElement>;
      const anonCheck = container.querySelector('#check-poll-anon') as HTMLInputElement;

      const opts: string[] = [];
      optInputs.forEach(i => {
        if (i.value.trim()) opts.push(i.value.trim());
      });

      if (qInput?.value.trim() && opts.length >= 2 && this.onCreatePoll) {
        this.onCreatePoll(qInput.value.trim(), opts, anonCheck?.checked || false);
        this.showCreateForm = false;
        this.render(container);
      }
    });

    container.querySelectorAll('.btn-vote-option').forEach(btn => {
      btn.addEventListener('click', () => {
        const pollId = (btn as HTMLElement).getAttribute('data-poll-id');
        const optIdx = parseInt((btn as HTMLElement).getAttribute('data-opt-idx') || '0', 10);
        if (pollId && this.onVote) {
          this.onVote(pollId, optIdx);
        }
      });
    });

    container.querySelectorAll('.btn-close-poll').forEach(btn => {
      btn.addEventListener('click', () => {
        const pollId = (btn as HTMLElement).getAttribute('data-poll-id');
        if (pollId && this.onClosePoll) {
          this.onClosePoll(pollId);
        }
      });
    });
  }
}
